import { Button, Skeleton, cn } from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { TriangleAlertIcon } from './icons';

/**
 * Layout shell shared by every Companies state (loaded grid, empty, loading,
 * error, forbidden). Keeps the sidebar visible so a consultor can always
 * navigate away regardless of which branch rendered.
 */
function CompaniesShell({
  children,
  onLogout,
}: {
  children: React.ReactNode;
  onLogout?: () => void;
}) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="home" onLogout={onLogout} />
      <main className="flex flex-1 flex-col gap-8 px-8 pt-8 pb-16">
        <header className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-3">
            <h1 className="text-4xl font-medium tracking-tight text-foreground">Empresas</h1>
          </div>
        </header>
        {children}
      </main>
    </div>
  );
}

export type LoadingFallbackView = 'cards' | 'table';

export interface CompaniesLoadingFallbackProps {
  /** Match the topology of the upcoming view so layout doesn't shift when data arrives. */
  view?: LoadingFallbackView;
}

/**
 * Skeleton rendered while the companies query is pending. The skeleton's shape
 * mirrors the active view (cards-grid or table-rows) so paint frames don't
 * cause layout shift when results arrive.
 */
export function CompaniesLoadingFallback({ view = 'cards' }: CompaniesLoadingFallbackProps) {
  if (view === 'table') {
    return (
      <CompaniesShell>
        <section
          aria-label="Carregando empresas"
          aria-busy="true"
          data-testid="recruiter-companies-loading"
          className="overflow-hidden rounded-lg border border-border bg-card shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)]"
        >
          {/* Header row */}
          <div className="flex items-center gap-6 border-b border-border px-6 py-3">
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-20" />
          </div>
          {/* Body rows */}
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-6 border-b border-border px-6 py-4 last:border-b-0"
            >
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 max-w-[40%] flex-1" />
              <Skeleton shape="pill" className="h-6 w-32" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-8 w-8" />
            </div>
          ))}
        </section>
      </CompaniesShell>
    );
  }

  return (
    <CompaniesShell>
      <section
        aria-label="Carregando empresas"
        aria-busy="true"
        data-testid="recruiter-companies-loading"
        className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'min-h-44 rounded-2xl border border-border bg-card p-6',
              'shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)]'
            )}
          >
            <div className="flex h-full flex-col justify-between gap-12">
              <div className="space-y-2">
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-5 w-3/4" />
              </div>
              <div className="flex items-center justify-between gap-3">
                <Skeleton shape="pill" className="h-6 w-32" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
          </div>
        ))}
      </section>
    </CompaniesShell>
  );
}

export interface CompaniesErrorFallbackProps {
  onRetry: () => void;
  onLogout?: () => void;
  /** Optional human-readable message; surfaced to support if shown to the user. */
  message?: string;
}

/**
 * Rendered when the companies query fails (5xx, network error). 4xx are
 * handled by the upstream `ForbiddenBoundary` (403) or by Suspense bubbling
 * (other 4xx → still ends here as last-resort).
 */
export function CompaniesErrorFallback({ onRetry, onLogout }: CompaniesErrorFallbackProps) {
  return (
    <CompaniesShell onLogout={onLogout}>
      <div role="alert" className="mt-16 flex w-full flex-col items-center gap-3">
        <div className="flex w-full flex-col items-center gap-5">
          <div
            aria-hidden="true"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-muted"
          >
            <TriangleAlertIcon className="h-6 w-6 text-foreground" />
          </div>
          <div className="flex w-full flex-col items-center gap-2 text-center">
            <h2 className="text-2xl font-semibold leading-none tracking-tight text-foreground">
              Não foi possível carregar as empresas
            </h2>
            <p className="text-base leading-normal text-muted-foreground">
              {'Houve um problema ao buscar as empresas. Tente novamente em alguns instantes.'}
            </p>
          </div>
        </div>
        <div className="flex flex-row items-center justify-center gap-2 self-stretch">
          <Button
            type="button"
            size="lg"
            onClick={onRetry}
            data-testid="recruiter-companies-error-retry"
          >
            Tentar novamente
          </Button>
        </div>
      </div>
    </CompaniesShell>
  );
}
